import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { EditOutlined as EditIcon } from '@mui/icons-material';
import { TableColumn, TableFilterRow, TableSearchField, useTableControls } from '../table/TableControls';
import ExportExclusionDialog, { setExportExclusion } from './ExportExclusionDialog';
import { bandText, effectiveRate, isBanded } from '../../utils/bandRate';

// One flagged product line of the "Rate looks wrong" rule, with its issue's
// and record's details alongside (api/DTOs/ValidationDto.cs).
export interface RateLine {
  id: string;
  recordId: string;
  subjectKey: string;
  productName: string;
  // The application rate; bandPercent is its record's.
  rate: number;
  rateUnitName: string;
  bandPercent?: number;
  kind: 'low' | 'high' | 'zero';
  reason: string;
  fileRateText?: string;
  fileRate?: number;
  dismissal?: { dismissedBy?: string; dateCreated: string; note?: string };
  // The line's whole record; these lines are listed only with dismissed ones.
  excludedFromExport: boolean;
  exportExclusionNote?: string;
  // The whole issue is dismissed, so restoring is done on the issue.
  issueDismissed: boolean;
  businessId: string;
  businessName: string;
  date: string;
  farmName?: string;
  fieldName?: string;
}

type RateMode = 'file' | 'set' | 'divide' | 'multiply' | 'keep';

const MODE_LABELS: Record<RateMode, string> = {
  file: "Use the file's rate",
  set: 'Set to',
  divide: 'Divide by',
  multiply: 'Multiply by',
  keep: 'Keep the rate',
};

const KIND_LABELS: Record<RateLine['kind'], string> = { low: 'Low', high: 'High', zero: 'No rate' };

const formatRate = (rate: number) => rate.toLocaleString(undefined, { maximumFractionDigits: 3 });

// The rate column keeps 3 decimals.
const roundRate = (rate: number) => Math.round(rate * 1000) / 1000;

// How the stored rate sits against what the file states. 1000× is its own
// value because it's Agworld's own slip, not a judgement call: pick it, tick
// them all and use the file's rate.
const againstFile = (line: RateLine) => {
  if (line.fileRate == null) return 'No file rate';
  const ratio = line.rate / line.fileRate;
  if (Math.abs(ratio - 1) <= 0.25) return 'Same';
  if (Math.abs(ratio / 1000 - 1) <= 0.25) return '1000× the file';
  return 'Differs';
};

// 4, >5, <0.1, >=2, <=2 or 2-4, as admin Records' Rate/ha filter.
const matchesRate = (rate: number, wanted: string) => {
  const range = wanted.match(/^([\d.,]+)\s*-\s*([\d.,]+)$/);
  if (range) return rate >= Number(range[1].replace(/,/g, '')) && rate <= Number(range[2].replace(/,/g, ''));
  const compare = wanted.match(/^(>=|<=|>|<)?\s*([\d.,]+)$/);
  if (!compare) return false;
  const value = Number(compare[2].replace(/,/g, ''));
  switch (compare[1]) {
    case '>=': return rate >= value;
    case '<=': return rate <= value;
    case '>': return rate > value;
    case '<': return rate < value;
    default: return rate === value;
  }
};

// What the rate gets decided on first, where the line is after: the grid is
// wider than the admin screen, and scrolls.
const columns: TableColumn<RateLine>[] = [
  { key: 'product', label: 'Product', getValue: (l) => l.productName },
  { key: 'rate', label: 'Application rate', getValue: (l) => formatRate(l.rate), placeholder: '>20, <0.1, 2-4', matches: (l, wanted) => matchesRate(l.rate, wanted) },
  { key: 'unit', label: 'Unit', getValue: (l) => l.rateUnitName, filter: 'select' },
  { key: 'band', label: 'Band', getValue: (l) => (isBanded(l.bandPercent) ? `${l.bandPercent}%` : ''), filter: 'select' },
  { key: 'effective', label: 'Effective rate', getValue: (l) => formatRate(effectiveRate(l.rate, l.bandPercent)), placeholder: '<0.1', matches: (l, wanted) => matchesRate(effectiveRate(l.rate, l.bandPercent), wanted) },
  { key: 'file', label: 'File says', getValue: (l) => l.fileRateText ?? '' },
  { key: 'against', label: 'Against file', getValue: againstFile, filter: 'select' },
  { key: 'kind', label: 'Flagged', getValue: (l) => KIND_LABELS[l.kind], filter: 'select' },
  { key: 'reason', label: 'Why', getValue: (l) => l.reason },
  { key: 'date', label: 'Date', getValue: (l) => new Date(l.date).toLocaleDateString('en-AU') },
  { key: 'farm', label: 'Farm', getValue: (l) => l.farmName ?? '' },
  { key: 'field', label: 'Field', getValue: (l) => l.fieldName ?? '' },
  { key: 'business', label: 'Business', getValue: (l) => l.businessName, filter: 'select' },
];

interface Props {
  lines: RateLine[];
  authHeaders: () => Record<string, string>;
  // Run validation again after a change.
  onChanged: () => Promise<void>;
  onEditRecord: (recordId: string, businessId: string) => void;
  onError: (message: string) => void;
}

// Data Validation's flagged rates, one row per product line across every
// issue: a record can hold other products, or the same one twice, so a rate is
// fixed or dismissed on the line and not on the record.
const ValidationRateLines: React.FC<Props> = ({ lines, authHeaders, onChanged, onEditRecord, onError }) => {
  const [sortKey, setSortKey] = useState('product');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastTicked = useRef<string | null>(null);

  const [updateOpen, setUpdateOpen] = useState(false);
  const [mode, setMode] = useState<RateMode>('set');
  const [value, setValue] = useState('');
  // The unit to change the lines to; blank leaves each line's own.
  const [unitId, setUnitId] = useState('');
  const [units, setUnits] = useState<{ id: string; name: string }[]>([]);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissNote, setDismissNote] = useState('');
  const [excludeTarget, setExcludeTarget] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // A new run's lines may have moved on; what was ticked goes with the old.
  useEffect(() => {
    setSelected(new Set());
  }, [lines]);

  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sortKey) ?? columns[0];
    const text = (l: RateLine) => column.getValue(l);
    const compare = (a: RateLine, b: RateLine) => {
      if (sortKey === 'rate') return a.rate - b.rate;
      if (sortKey === 'band') return (a.bandPercent ?? 100) - (b.bandPercent ?? 100);
      if (sortKey === 'effective') return effectiveRate(a.rate, a.bandPercent) - effectiveRate(b.rate, b.bandPercent);
      if (sortKey === 'date') return a.date.localeCompare(b.date);
      return text(a).localeCompare(text(b), undefined, { numeric: true });
    };
    // Lines that tie stay grouped by product, biggest rate first, so sorting
    // Product lines up each product's 2,000s, then its 1,000s.
    return [...lines].sort((a, b) =>
      (sortDir === 'asc' ? compare(a, b) : -compare(a, b)) || a.productName.localeCompare(b.productName) || b.rate - a.rate);
  }, [lines, sortKey, sortDir]);

  const table = useTableControls(sorted, columns);

  // Only what the filters leave counts, so a line ticked and then filtered
  // away can't be changed unseen.
  const ticked = useMemo(() => table.filteredItems.filter((l) => selected.has(l.id)), [table.filteredItems, selected]);
  const toDismiss = ticked.filter((l) => !l.dismissal && !l.issueDismissed && !l.excludedFromExport);
  const toRestore = ticked.filter((l) => l.dismissal && !l.issueDismissed);
  // Exclusion is on the record, and one record can have several lines ticked.
  const toExclude = Array.from(new Set(ticked.filter((l) => !l.excludedFromExport).map((l) => l.recordId)));
  const toInclude = Array.from(new Set(ticked.filter((l) => l.excludedFromExport).map((l) => l.recordId)));

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Shift-click ticks (or unticks) every line from the last one clicked, in
  // the order shown, across pages.
  const tick = (line: RateLine, shift: boolean) => {
    const items = table.filteredItems;
    const index = items.findIndex((l) => l.id === line.id);
    const lastIndex = shift && lastTicked.current ? items.findIndex((l) => l.id === lastTicked.current) : -1;
    const [from, to] = lastIndex >= 0 ? [Math.min(lastIndex, index), Math.max(lastIndex, index)] : [index, index];
    setSelected((prev) => {
      const next = new Set(prev);
      const on = !next.has(line.id);
      for (let i = from; i <= to; i++) {
        if (on) next.add(items[i].id); else next.delete(items[i].id);
      }
      return next;
    });
    lastTicked.current = line.id;
  };

  const openUpdate = () => {
    // The file's rate first when every ticked line has one to take.
    setMode(ticked.every((l) => l.fileRate != null) ? 'file' : 'set');
    setValue('');
    setUnitId('');
    setUpdateOpen(true);
    if (units.length > 0) return;
    (async () => {
      const res = await fetch('/api/units', { headers: authHeaders() });
      if (res.ok) setUnits((await res.json()).filter((u: any) => !u.archivedDate).map((u: any) => ({ id: u.id, name: u.name })));
    })();
  };

  const newUnitName = units.find((u) => u.id === unitId)?.name;
  const parsedValue = Number(value);
  const newRate = (line: RateLine): number | null => {
    if (mode === 'file') return line.fileRate ?? null;
    if (mode === 'keep') return unitId ? line.rate : null;
    if (!(parsedValue > 0)) return null;
    if (mode === 'set') return parsedValue;
    return mode === 'divide' ? line.rate / parsedValue : line.rate * parsedValue;
  };

  // "2,000 Kg/ha → 2 Kg/ha", most lines first, to check before anything
  // changes: a bulk change has no undo.
  const preview = useMemo(() => {
    const counts = new Map<string, { from: number; fromUnit: string; to: number; toUnit: string; count: number }>();
    if (!updateOpen) return { changes: [], skipped: 0 };
    let skipped = 0;
    ticked.forEach((line) => {
      const after = newRate(line);
      const to = after == null ? null : roundRate(after);
      if (to == null || (to <= 0 && !(mode === 'keep' && to === 0))) {
        skipped++;
        return;
      }
      const toUnit = newUnitName ?? line.rateUnitName;
      if (to === line.rate && toUnit === line.rateUnitName) return;
      const key = `${line.rate}|${line.rateUnitName}|${to}|${toUnit}`;
      const change = counts.get(key) ?? { from: line.rate, fromUnit: line.rateUnitName, to, toUnit, count: 0 };
      change.count++;
      counts.set(key, change);
    });
    return { changes: Array.from(counts.values()).sort((a, b) => b.count - a.count), skipped };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateOpen, ticked, mode, value, unitId, units]);

  const post = async (url: string, body: object) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    const result = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(result.error || 'The change failed');
    return result;
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const lineIds = ticked.map((l) => l.id);
      const result = await post('/api/records/lines/bulk-rate', {
        lineIds,
        mode,
        value: mode === 'file' || mode === 'keep' ? null : parsedValue,
        rateUnitId: unitId || null,
      });
      setUpdateOpen(false);
      setMessage(`Updated ${result.updatedCount} line${result.updatedCount === 1 ? '' : 's'}${result.skippedCount ? `, skipped ${result.skippedCount}` : ''}.`);
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = async () => {
    setSaving(true);
    try {
      await post('/api/validation/dismissals/lines', { ruleKey: 'rate-outlier', subjectKeys: toDismiss.map((l) => l.subjectKey), note: dismissNote });
      setDismissOpen(false);
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async () => {
    try {
      await post('/api/validation/dismissals/lines/restore', { ruleKey: 'rate-outlier', subjectKeys: toRestore.map((l) => l.subjectKey) });
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleInclude = async () => {
    try {
      await setExportExclusion(toInclude, false, null, authHeaders);
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const allTicked = table.filteredItems.length > 0 && ticked.length === table.filteredItems.length;

  return (
    <Box>
      {message && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setMessage(null)}>{message}</Alert>}
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={1.5}>
        <TableSearchField value={table.search} onChange={table.setSearch} placeholder="Search lines…" />
        <Typography variant="caption" color="text.secondary">
          {ticked.length > 0
            ? `${ticked.length} of ${table.filteredItems.length} selected`
            : 'Tick lines to update or dismiss them (shift-click ticks a run; the header box ticks everything the filters leave)'}
        </Typography>
        {ticked.length > 0 && (
          <Button size="small" variant="outlined" startIcon={<EditIcon fontSize="small" />} onClick={openUpdate}>
            Update rate or unit on {ticked.length} line{ticked.length === 1 ? '' : 's'}
          </Button>
        )}
        {toDismiss.length > 0 && (
          <Button size="small" variant="outlined" onClick={() => { setDismissNote(''); setDismissOpen(true); }}>
            Dismiss {toDismiss.length} line{toDismiss.length === 1 ? '' : 's'}
          </Button>
        )}
        {toExclude.length > 0 && (
          <Button size="small" variant="outlined" onClick={() => setExcludeTarget(toExclude)}>
            Exclude {toExclude.length} record{toExclude.length === 1 ? '' : 's'} from export
          </Button>
        )}
        {toRestore.length > 0 && (
          <Button size="small" onClick={handleRestore}>Restore {toRestore.length} line{toRestore.length === 1 ? '' : 's'}</Button>
        )}
        {toInclude.length > 0 && (
          <Button size="small" onClick={handleInclude}>Include {toInclude.length} record{toInclude.length === 1 ? '' : 's'} in export</Button>
        )}
        {ticked.length > 0 && <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>}
      </Box>
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, fontSize: '0.8125rem' }, '& .MuiTableCell-head': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    inputProps={{ 'aria-label': 'Select every line the filters leave' }}
                    checked={allTicked}
                    indeterminate={ticked.length > 0 && !allTicked}
                    onChange={(e) => setSelected(new Set(e.target.checked ? table.filteredItems.map((l) => l.id) : []))}
                  />
                </TableCell>
                {columns.map((c) => (
                  <TableCell key={c.key} align={['rate', 'band', 'effective'].includes(c.key) ? 'right' : undefined} sortDirection={sortKey === c.key ? sortDir : false}>
                    <TableSortLabel active={sortKey === c.key} direction={sortKey === c.key ? sortDir : 'asc'} onClick={() => handleSort(c.key)}>
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
              <TableFilterRow columns={columns} state={table} leadingCells={1} trailingCells={1} />
            </TableHead>
            <TableBody>
              {table.filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 2} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No lines match.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {table.pageItems.map((line) => (
                <TableRow
                  key={line.id}
                  hover
                  selected={selected.has(line.id)}
                  title="Double-click to edit the record"
                  sx={{ cursor: 'pointer', ...(line.dismissal || line.issueDismissed || line.excludedFromExport ? { opacity: 0.55 } : {}) }}
                  onDoubleClick={() => onEditRecord(line.recordId, line.businessId)}
                >
                  <TableCell padding="checkbox" onDoubleClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      inputProps={{ 'aria-label': 'Select line' }}
                      checked={selected.has(line.id)}
                      // React's checkbox onChange is a click, so it carries shiftKey.
                      onChange={(e) => tick(line, (e.nativeEvent as MouseEvent).shiftKey)}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 260 }}>
                    {line.productName}
                    {line.dismissal && (
                      <Tooltip title={`Dismissed${line.dismissal.dismissedBy ? ` by ${line.dismissal.dismissedBy}` : ''} on ${new Date(line.dismissal.dateCreated).toLocaleDateString('en-AU')}${line.dismissal.note ? ` — ${line.dismissal.note}` : ''}`}>
                        <Typography variant="caption" color="text.secondary" display="block">Dismissed</Typography>
                      </Tooltip>
                    )}
                    {line.excludedFromExport && (
                      <Tooltip title={line.exportExclusionNote ?? ''}>
                        <Typography variant="caption" color="text.secondary" display="block">Excluded from export</Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatRate(line.rate)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{line.rateUnitName}</TableCell>
                  <TableCell align="right">{bandText(line.bandPercent)}</TableCell>
                  <TableCell align="right">{formatRate(effectiveRate(line.rate, line.bandPercent))}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {line.fileRateText ?? '—'}
                    {line.fileRate != null && againstFile(line) !== 'Same' && (
                      <Typography variant="caption" color="text.secondary" display="block">= {formatRate(line.fileRate)} {line.rateUnitName}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{againstFile(line)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{KIND_LABELS[line.kind]}</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>{line.reason}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(line.date).toLocaleDateString('en-AU')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{line.farmName ?? '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{line.fieldName ?? '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{line.businessName}</TableCell>
                  <TableCell />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" {...table.paginationProps} />
      </Paper>

      <Dialog open={updateOpen} onClose={() => !saving && setUpdateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update {ticked.length} line{ticked.length === 1 ? '' : 's'}</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={1.5} mt={1} mb={2}>
            <FormControl fullWidth>
              <InputLabel id="rate-update-mode">Change</InputLabel>
              <Select labelId="rate-update-mode" label="Change" value={mode} onChange={(e) => setMode(e.target.value as RateMode)}>
                {(Object.keys(MODE_LABELS) as RateMode[]).map((m) => (
                  <MenuItem key={m} value={m} disabled={m === 'file' && !ticked.some((l) => l.fileRate != null)}>{MODE_LABELS[m]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {mode !== 'file' && mode !== 'keep' && (
              <TextField
                autoFocus
                type="number"
                label={mode === 'set' ? 'Rate' : 'Value'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                inputProps={{ min: 0, step: 'any' }}
                sx={{ minWidth: 120 }}
              />
            )}
          </Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="rate-update-unit" shrink>Unit</InputLabel>
            <Select
              labelId="rate-update-unit"
              label="Unit"
              notched
              displayEmpty
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
            >
              <MenuItem value=""><em>Leave as is</em></MenuItem>
              {units.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {preview.changes.length > 0 ? 'This will change:' : 'Nothing to change yet.'}
          </Typography>
          {preview.changes.slice(0, 10).map((c) => (
            <Typography key={`${c.from}|${c.fromUnit}|${c.to}|${c.toUnit}`} variant="body2">
              {formatRate(c.from)} {c.fromUnit} → {formatRate(c.to)} {c.toUnit} ({c.count} line{c.count === 1 ? '' : 's'})
            </Typography>
          ))}
          {preview.changes.length > 10 && (
            <Typography variant="body2" color="text.secondary">and {preview.changes.length - 10} more rates</Typography>
          )}
          {preview.skipped > 0 && value !== '0' && (mode === 'file' || parsedValue > 0) && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {preview.skipped} line{preview.skipped === 1 ? '' : 's'} will be left as {preview.skipped === 1 ? 'it is' : 'they are'}
              {mode === 'file' ? ': no rate per hectare in the file.' : ': the new rate rounds to 0.'}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            These are application rates, before any band. Changes go on these lines and their import rows only. A new unit doesn&apos;t convert the number. There&apos;s no undo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={saving || preview.changes.length === 0}>
            {saving ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <ExportExclusionDialog
        recordIds={excludeTarget}
        authHeaders={authHeaders}
        onClose={() => setExcludeTarget(null)}
        onDone={onChanged}
        onError={onError}
      />

      <Dialog open={dismissOpen} onClose={() => !saving && setDismissOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dismiss {toDismiss.length} line{toDismiss.length === 1 ? '' : 's'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            These lines stop being flagged while their rate stays the same. Other products on the same records are still checked.
          </Typography>
          <TextField autoFocus fullWidth multiline minRows={2} label="Note (optional)" value={dismissNote} onChange={(e) => setDismissNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleDismiss} disabled={saving}>{saving ? 'Dismissing...' : 'Dismiss'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ValidationRateLines;
